var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { checkUserType } from "healthex-common/checkusertype";
import { getDataConsentTypeForProject, isConsentDataSensitivityScopeValidForConsent } from "healthex-common/consent.dtos";
import { ConsentDataResourceScope, ConsentDataSensitivityScope } from "healthex-common/consentscopes.dtos";
import { ProjectPageName } from "healthex-common/project.data.dtos";
import { ProjectType, StudyAccessData, StudyType } from "healthex-common/study.dtos";
import { UserType } from "healthex-common/user.dtos";
import { enumValues, filterFalse, omit, willNeverHappen } from "healthex-common/utils";
import { computed } from "mobx";
import { NOT_LOADED } from "../../../Infra/RemoteData";
import { AddJoinCommonRequirementsWizardViewModel } from "../../AddProjectJoinCommon/AddJoinCommonRequirementsWizardViewModel";
export class AddProjectRequirementsWizardViewModel extends AddJoinCommonRequirementsWizardViewModel {
    constructor(opts) {
        super(opts.container, opts.container.addProjectRequirementsWizardUIManager, opts.viewType, opts.viewType === "CREATE" ? "WELCOME" : "IMPORT_METHOD", {
            projectType: ProjectType.GENERAL_DATA_USE,
            protocolDocument: null,
            protocolDocumentUrl: null,
            title: "",
            studyType: null,
            accessData: null,
            isSponsor: null,
            teamMembers: [],
            hipaaOrganizationLegalName: opts.container.authManager.orgName,
            hipaaOrganizationLegalNameConfirmed: false,
        });
        this.wasTeamMembersSkipped = false;
        this.addProjectUIManager = opts.container.addProjectUIManager;
        this.authManager = opts.container.authManager;
        this.studyManager = opts.container.studyManager;
        this.organizationsManager = opts.container.organizationsManager;
        this.study = opts.study;
        if (this.isPdda) {
            this.formModel.updateInput({ accessData: StudyAccessData.FULL_RECORD_IDENTIFIED });
        }
    }
    run() {
        super.run();
    }
    stop() {
        super.stop();
        this.disposers.forEach((disposer) => disposer());
        this.disposers = [];
    }
    get hasStudyDataBeenEntered() {
        const study = this.study;
        if (!study)
            return null;
        const studyFields = [
            "briefSummary",
            "description",
            "clinicalTrialPhase",
            "primaryDiseaseOrConditions",
            "interventions",
            "outcomes",
            "inclusionExclusionCriteria",
            "targetNumberOfPatients",
            "startDate",
            "completionDate",
            "primaryCompletionDate",
            "sponsorPrimaryContactName",
            "sponsorPrimaryContactTitle",
            "sponsorPrimaryContactEmail",
            "sponsorPrimaryContactPhone",
            "sponsorPrimaryContactPhoneExt",
            "funderType",
            "studyIdentifier",
            "webpageLink",
            "protocolNumber",
        ];
        if (!study.isSponsor)
            studyFields.push("sponsorOrganizationName");
        return studyFields.some((key) => {
            const value = study[key];
            if (value instanceof Array) {
                return value.filter((item) => !!item).length;
            }
            return value || value === false || value === 0;
        });
    }
    get isPdda() {
        return this.authManager.userType === UserType.EXTERNAL_RESEARCHER;
    }
    get pages() {
        switch (this.viewType) {
            case "CREATE": {
                return filterFalse([
                    "WELCOME",
                    this.isPdda && "LEGAL_NAME",
                    "STUDY_NAME",
                    "TEAM_MEMBERS",
                    "SUCCESS",
                ]);
            }
        }
        return willNeverHappen(this.viewType);
    }
    get showProgressBar() {
        return this.currentPage !== "WELCOME" && this.viewType === "CREATE";
    }
    get progressBarPercentage() {
        const progressSteps = [];
        if (this.isPdda)
            progressSteps.push("LEGAL_NAME");
        progressSteps.push("STUDY_NAME");
        progressSteps.push("TEAM_MEMBERS", "SUCCESS");
        const onePagePercentage = 100 / progressSteps.length;
        const progressStepIndex = progressSteps.indexOf(this.currentPage);
        return progressStepIndex === -1 ? 0 : (progressStepIndex + 1) * onePagePercentage;
    }
    get dialogTitleInfo() {
        switch (this.currentPage) {
            case "WELCOME":
                return { title: "Start with project requirements" };
            case "PROJECT_TYPE":
                return { title: "What type of project do you need" };
            case "LEGAL_NAME":
                return { title: "Confirm the organization’s legal name" };
            case "STUDY_NAME":
                return { title: "What's the name of this project?" };
            case "TEAM_MEMBERS":
                return { title: `Who else at ${this.authManager.orgName} is working on this project?` };
            case "SUCCESS":
                return { title: "You're ready to add the project!" };
        }
        return willNeverHappen(this.currentPage);
    }
    get showBackButton() {
        return this.currentPageIndex > 0;
    }
    get showSkipButton() {
        return this.currentPage === "TEAM_MEMBERS";
    }
    get nextButtonText() {
        switch (this.currentPage) {
            case "WELCOME":
                return "Get started";
            case "TEAM_MEMBERS":
                return "Done";
            case "SUCCESS":
                return "Continue to add project";
            default:
                return "Next";
        }
    }
    get nextButtonEnabled() {
        switch (this.currentPage) {
            case "WELCOME":
                return true;
            case "PROJECT_TYPE":
                return this.formModel.input.projectType !== null;
            case "LEGAL_NAME":
                return !!this.formModel.input.hipaaOrganizationLegalName && this.formModel.input.hipaaOrganizationLegalNameConfirmed;
            case "STUDY_NAME":
                return !!this.formModel.input.title;
            case "TEAM_MEMBERS":
                return true;
            case "SUCCESS": {
                const pddaCheck = this.isPdda
                    ? !!this.formModel.input.hipaaOrganizationLegalName &&
                        this.formModel.input.hipaaOrganizationLegalNameConfirmed &&
                        this.formModel.input.projectType !== null
                    : true;
                return !!this.formModel.input.title && this.formModel.input.accessData !== null && pddaCheck;
            }
        }
        return willNeverHappen(this.currentPage);
    }
    get nextButtonChevronVisible() {
        return this.currentPage !== "WELCOME" && this.currentPage !== "SUCCESS";
    }
    get organization() {
        const org = this.organizationsManager.myOrganization.value;
        if (!org || org === NOT_LOADED)
            return null;
        if (org.type !== UserType.HEALTHCARE_ORGANIZATION && org.type !== UserType.EXTERNAL_RESEARCHER)
            return null;
        return org;
    }
    get organizationDefaultRequire21CFRPart11ForInformedConsents() {
        var _a, _b;
        return (_b = (_a = this.organization) === null || _a === void 0 ? void 0 : _a.defaultRequire21CFRPart11ForInformedConsents) !== null && _b !== void 0 ? _b : false;
    }
    updateProtocolDocument(protocolDocument) {
        this.formModel.input.protocolDocument = protocolDocument;
    }
    get uploadingProgressBarPercentage() {
        return this.progressBars[0];
    }
    get extractingProgressBarPercentage() {
        return this.progressBars[1];
    }
    get processingProgressBarPercentage() {
        return this.progressBars[2];
    }
    async doSave() {
        var _a;
        const projectType = (_a = this.formModel.input.projectType) !== null && _a !== void 0 ? _a : ProjectType.GENERAL_DATA_USE;
        const studyType = StudyType.OTHER;
        const accessData = this.formModel.input.accessData;
        const hipaaOrganizationLegalName = this.formModel.input.hipaaOrganizationLegalName;
        const hipaaOrganizationLegalNameConfirmed = this.formModel.input.hipaaOrganizationLegalNameConfirmed;
        const isSponsor = this.formModel.input.isSponsor;
        if (this.authManager.id === null || projectType === null || studyType === null || accessData === null)
            return;
        if (this.isPdda) {
            if (!hipaaOrganizationLegalName || !hipaaOrganizationLegalNameConfirmed)
                return;
        }
        const requiredSensitivityScopesForPdda = enumValues(ConsentDataSensitivityScope).filter((scope) => isConsentDataSensitivityScopeValidForConsent(getDataConsentTypeForProject({ pdda: this.isPdda }), scope));
        const saveInput = {
            ...omit(this.formModel.input, "teamMembers"),
            ownerId: this.authManager.id,
            projectType,
            studyType,
            accessData,
            require21CFRPart11ForInformedConsents: false,
            informedConsentSectionLocked: false,
            consentDataResourceScopes: this.isPdda ? enumValues(ConsentDataResourceScope) : [],
            consentDataSensitivityScopes: this.isPdda ? requiredSensitivityScopesForPdda : [ConsentDataSensitivityScope.NORMAL],
            hipaaOrganizationLegalName: this.isPdda ? hipaaOrganizationLegalName : undefined,
            hipaaOrganizationLegalNameConfirmed: this.isPdda ? hipaaOrganizationLegalNameConfirmed : undefined,
            fullRecordWithoutRestrictedData: this.isPdda ? true : undefined,
            isSponsor,
            suppressNotifications: false,
        };
        const studyId = await this.studyManager.createStudy(saveInput);
        if (studyId === null) {
            window.location.reload();
            return;
        }
        if (!this.wasTeamMembersSkipped) {
            await Promise.all(this.formModel.input.teamMembers.map(async (teamMember) => {
                await this.studyManager.createOrUpdateStudyUser(studyId, teamMember.user, {});
            }));
        }
        await this.addProjectUIManager.goToPage(ProjectPageName.PROJECT_DETAILS, undefined, studyId);
    }
    async goToNextPage() {
        if (this.currentPage === "SUCCESS") {
            await this.doSave();
            return;
        }
        if (this.currentPageIndex === this.pages.length - 1)
            return;
        if (this.currentPage === "TEAM_MEMBERS")
            this.wasTeamMembersSkipped = false;
        this.currentPage = this.pages[this.currentPageIndex + 1];
    }
    async skipPage() {
        const previousCurrentPage = this.currentPage;
        void this.goToNextPage();
        if (previousCurrentPage === "TEAM_MEMBERS")
            this.wasTeamMembersSkipped = true;
    }
    get selfStudyUser() {
        if (this.organizationsManager.myOrganization.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.organizationsManager.myUser.value === NOT_LOADED)
            return NOT_LOADED;
        if (this.organizationsManager.myOrganization.value === null)
            return null;
        if (this.organizationsManager.myUser.value === null)
            return null;
        if (this.authManager.userType === null)
            return null;
        const myOrg = checkUserType(this.organizationsManager.myOrganization.value, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
        const myUser = checkUserType(this.organizationsManager.myUser.value, UserType.HEALTHCARE_ORGANIZATION, UserType.EXTERNAL_RESEARCHER);
        if (!myOrg || !myUser)
            return null;
        return {
            user: myUser,
            organization: myOrg,
        };
    }
    getAnVsAString(followingString) {
        if (!followingString.trim())
            return "";
        return ["a", "e", "i", "o", "u"].includes(followingString[0].toLowerCase()) ? "an" : "a";
    }
}
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "hasStudyDataBeenEntered", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "isPdda", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "pages", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "showProgressBar", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "progressBarPercentage", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "dialogTitleInfo", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "showBackButton", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "showSkipButton", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "nextButtonText", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "nextButtonEnabled", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "nextButtonChevronVisible", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "organization", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "organizationDefaultRequire21CFRPart11ForInformedConsents", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "uploadingProgressBarPercentage", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "extractingProgressBarPercentage", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "processingProgressBarPercentage", null);
__decorate([
    computed
], AddProjectRequirementsWizardViewModel.prototype, "selfStudyUser", null);
